<script setup lang="ts">
/** Shared by /login and /signup — the design's "Log in" screen. */
const props = defineProps<{ mode: 'login' | 'signup' }>()

const supabase = useSupabaseClient()
const { $api } = useApi()
const redirect = useSupabaseCookieRedirect()
const signupEmail = useState<string>('signup-email', () => '')

const email = ref('')
const password = ref('')
const confirmPassword = ref('')
const error = ref('')
const info = ref('')
const loading = ref(false)

const isLogin = computed(() => props.mode === 'login')
const mismatch = computed(() => confirmPassword.value !== '' && confirmPassword.value !== password.value)
const canSubmit = computed(
  () =>
    Boolean(email.value && password.value) &&
    (isLogin.value || (isStrongPassword(password.value) && confirmPassword.value === password.value)),
)

async function finish() {
  try {
    // Create/update the local user record in Regotiate's DB
    await $api('/api/auth/callback', { method: 'POST' })
  } catch {
    // Non-fatal — the API also creates the user lazily on first authenticated write
  }
  await navigateTo(redirect.pluck() || '/')
}

async function submit() {
  if (!canSubmit.value) return
  loading.value = true
  error.value = ''
  info.value = ''
  try {
    if (isLogin.value) {
      const { error: err } = await supabase.auth.signInWithPassword({ email: email.value, password: password.value })
      if (err) throw err
      await finish()
    } else {
      const { data, error: err } = await supabase.auth.signUp({
        email: email.value,
        password: password.value,
        options: { emailRedirectTo: `${window.location.origin}/confirm` },
      })
      if (err) throw err
      if (data.session) await finish()
      else {
        signupEmail.value = email.value
        await navigateTo('/check-email')
      }
    }
  } catch (err) {
    error.value = (err as Error).message
  } finally {
    loading.value = false
  }
}

async function google() {
  error.value = ''
  const { error: err } = await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo: `${window.location.origin}/confirm` },
  })
  if (err) error.value = err.message
}

async function forgot() {
  error.value = ''
  info.value = ''
  if (!email.value) {
    error.value = 'Enter your email first, then tap “Forgot password”.'
    return
  }
  const { error: err } = await supabase.auth.resetPasswordForEmail(email.value, {
    redirectTo: `${window.location.origin}/reset-password`,
  })
  if (err) error.value = err.message
  else info.value = 'If that email has an account, a reset link is on its way.'
}
</script>

<template>
  <div class="container container--narrow page auth">
    <img src="/images/brand/logo-mark-black.svg" alt="Regotiate" width="54" height="54" class="auth__logo" />
    <h1 class="h1">{{ isLogin ? 'Log in' : 'Create an account' }}</h1>
    <p class="muted auth__lead">
      {{ isLogin ? 'Welcome back! Please enter your details.' : 'Join deals, share with friends and save.' }}
    </p>

    <form class="auth__form" novalidate @submit.prevent="submit">
      <label class="field">
        <span class="label">Email</span>
        <input v-model="email" class="input" type="email" autocomplete="email" placeholder="Enter your email" required />
      </label>
      <label class="field">
        <span class="label">Password</span>
        <PasswordInput
          v-model="password"
          :autocomplete="isLogin ? 'current-password' : 'new-password'"
          :placeholder="isLogin ? '••••••••' : 'Create a password'"
        />
      </label>
      <PasswordRules v-if="!isLogin" :password="password" />
      <label v-if="!isLogin" class="field">
        <span class="label">Confirm password</span>
        <PasswordInput
          v-model="confirmPassword"
          autocomplete="new-password"
          placeholder="Repeat your password"
          :invalid="mismatch"
        />
      </label>
      <p v-if="!isLogin && mismatch" class="form-error" role="alert">Passwords don’t match.</p>

      <div v-if="isLogin" class="auth__row">
        <button type="button" class="btn-link btn" @click="forgot">Forgot password</button>
      </div>

      <p v-if="error" class="form-error" role="alert">{{ error }}</p>
      <p v-if="info" class="text-sm auth__info" role="status">{{ info }}</p>

      <button type="submit" class="btn btn-primary btn-block" :disabled="loading || !canSubmit">
        {{ loading ? 'Please wait…' : isLogin ? 'Sign in' : 'Sign up' }}
      </button>
      <button type="button" class="btn btn-secondary btn-block" @click="google">
        <img src="/images/icons/google.svg" alt="" width="24" height="24" />
        {{ isLogin ? 'Sign in' : 'Sign up' }} with Google
      </button>
    </form>

    <p class="text-sm muted auth__switch">
      <template v-if="isLogin">Don’t have an account? <NuxtLink to="/signup" class="btn-link">Sign up</NuxtLink></template>
      <template v-else>Already have an account? <NuxtLink to="/login" class="btn-link">Log in</NuxtLink></template>
    </p>
  </div>
</template>

<style scoped>
.auth {
  padding-top: 48px;
}

.auth__logo {
  margin-bottom: 24px;
}

.auth__lead {
  margin-top: 8px;
}

.auth__form {
  display: grid;
  gap: 16px;
  margin-top: 32px;
}

.auth__form .field + .field {
  margin-top: 0;
}

.auth__row {
  display: flex;
  justify-content: flex-end;
}

.auth__info {
  color: var(--success-700);
}

.auth__switch {
  margin-top: 32px;
  text-align: center;
}

.auth__switch a {
  font-weight: 600;
}
</style>
